import type { LucideIcon } from "lucide-react";
import { BookOpen, Briefcase, Users } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { GlassCard, RevealItem, RevealList, SlideShell, useAccent } from "@/ui";

interface RaterCard {
  heading: string;
  icon: LucideIcon;
  body: string;
  /** Emphasised line in a bordered callout. */
  callout: string;
  /** Optional chip row (card 2: what the guidelines cover). */
  chips?: string[];
}

const CARDS: RaterCard[] = [
  {
    heading: "Human raters, published guidelines",
    icon: Users,
    body: "Google uses human raters to calibrate and evaluate its systems.",
    callout: "They do not directly move your ranking with a single vote.",
  },
  {
    heading: "What the guidelines cover",
    icon: BookOpen,
    body: "The guidelines are public — and useful reading.",
    chips: ["E-E-A-T", "Page quality", "Needs-met"],
    callout: "Align your content with what “helpful” means.",
  },
  {
    heading: "Background, not a job pitch",
    icon: Briefcase,
    body: "Vendors sometimes supply rater work; details change over time.",
    callout: "Treat it as background on how quality is studied.",
  },
];

/** c6-quality-raters — how humans fit into Google's quality loop. */
export function QualityRatersSlide({ active }: SlideProps) {
  const [from] = useAccent();
  return (
    <SlideShell chapter={6} title="Search quality raters and guidelines">
      <RevealList active={active} stagger={0.16} className="grid w-full grid-cols-3 gap-8">
        {CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <RevealItem key={card.heading} className="h-full">
              <GlassCard className="flex h-full flex-col gap-6 p-9">
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5">
                  <Icon size={34} strokeWidth={1.75} style={{ color: from }} />
                </span>
                <h2 className="font-display text-3xl font-semibold leading-snug text-white">
                  {card.heading}
                </h2>
                <p className="text-2xl leading-snug text-white/80">{card.body}</p>
                {card.chips && (
                  <div className="flex flex-wrap gap-3">
                    {card.chips.map((chip) => (
                      <span
                        key={chip}
                        className="rounded-full border border-white/15 bg-white/5 px-5 py-2 text-xl font-medium text-white/90"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-auto rounded-xl border border-white/10 bg-white/5 p-5 text-2xl font-semibold leading-snug text-white">
                  {card.callout}
                </p>
              </GlassCard>
            </RevealItem>
          );
        })}
      </RevealList>
    </SlideShell>
  );
}
